import {
  Analytics,
  Brightness6,
  Cast,
  CropOriginal,
  DeleteSweep,
  Description,
  DeviceHub,
  Keyboard,
  Layers,
  LayersClear,
  LiveTv,
  OfflineBolt,
  PhotoCamera,
  PlayArrow,
  PowerSettingsNew,
  RestartAlt,
  Schedule,
  Search,
  Sell,
  Send,
  Settings,
  SettingsRemote,
  Tv,
  TvOff,
  VolumeOff,
  VolumeUp,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSendDeviceCommand } from '~/api/devices';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { KeyboardOff, SettingsRemoteOff } from '~/components/icons';
import { TooltipMenuItem, focusableDisabledItems } from '~/components/menu-item';
import { useAppContext } from '~/contexts';
import { DeviceCommandKind, type DeviceBulkSelectionInput } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';

// Handler shape: caller opens its dialog with the given selection, and runs
// onSuccess after the dialog completes (e.g. to clear bulk-selected ids).
// CommandPopover binds selection + onComplete at click time, so wrappers can
// pass their handlers through unchanged. Items whose handler is undefined are
// hidden.
export type DeviceActionHandler = (
  selected: DeviceBulkSelectionInput,
  onSuccess?: () => void,
) => void;

export type DeviceCommandHandlers = {
  onAddToGroup?: DeviceActionHandler;
  onBrightness?: DeviceActionHandler;
  onPlayContent?: DeviceActionHandler;
  onPlayOverlay?: DeviceActionHandler;
  onScheduleShow?: DeviceActionHandler;
  onSendArbitrary?: DeviceActionHandler;
  onTagContent?: DeviceActionHandler;
  onTriggerView?: DeviceActionHandler;
  onTuneChannel?: DeviceActionHandler;
  onUpdateSettings?: DeviceActionHandler;
  onUpdateSoftware?: DeviceActionHandler;
  onVolume?: DeviceActionHandler;
};

export type CommandPopoverProps = DeviceCommandHandlers & {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  selected: DeviceBulkSelectionInput;
  onComplete?: () => void;
  subject?: string;
  // Id of the button that opened the popover, when it should name the menu.
  labelledBy?: string;
  // Id for the menu, matching the opener's aria-controls.
  menuId?: string;
  // Per-item disabled reasons keyed by item key. When set, the item renders
  // disabled with a tooltip showing the reason. Lets consumers gate items
  // based on per-device runtime state without removing them from the menu.
  disabledItems?: Record<string, string>;
};

export type Item = {
  key: string;
  label: string;
  Icon: SvgIconComponent;
  adminOnly?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onClick?: DeviceActionHandler;
  command?: DeviceCommandKind;
  payload?: Record<string, unknown>;
  confirm?: { title: string; prompt: string };
};

export type Group = {
  key: string;
  title: string;
  adminOnly?: boolean;
  items: Item[];
};

const arbitrary = (key: string): Pick<Item, 'command' | 'payload'> => ({
  command: DeviceCommandKind.Arbitrary,
  payload: { arbitrary: key },
});

export const CommandPopover = ({
  anchorEl,
  open,
  onClose,
  selected,
  onAddToGroup,
  onBrightness,
  onPlayContent,
  onPlayOverlay,
  onSendArbitrary,
  onScheduleShow,
  onTagContent,
  onTriggerView,
  onTuneChannel,
  onUpdateSettings,
  onUpdateSoftware,
  onVolume,
  onComplete,
  subject,
  labelledBy,
  menuId,
  disabledItems,
}: CommandPopoverProps) => {
  const { currentNetwork, currentUser } = useAppContext();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const [sendCommand] = useSendDeviceCommand();
  const [confirm, confirmProps] = useConfirmDialog();
  const [confirmText, setConfirmText] = useState<{ title: string; prompt: string }>({
    title: '',
    prompt: '',
  });

  // Reset search whenever the popover closes so reopening starts fresh.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const runCommand = useCallback(
    async (item: Item) => {
      onClose();
      if (item.confirm) {
        setConfirmText(item.confirm);
        const ok = await confirm();
        if (!ok) return;
      }
      if (item.command === undefined) return;
      await sendCommand({
        variables: {
          input: { command: item.command, payload: item.payload ?? {}, selected },
        },
      });
      onComplete?.();
    },
    [confirm, onClose, onComplete, selected, sendCommand],
  );

  const groups = useMemo<Group[]>(
    () => [
      // Manage section: non-command CRUD/metadata operations sit at the top so
      // the popover starts with the broader operational stuff users recognize
      // from other list pages, then descends into actual device commands.
      {
        key: 'manage',
        title: t('pages.devices.bulk_actions.groups.manage'),
        items: [
          { key: 'tag', label: t('common.tags.add_tag'), Icon: Sell, onClick: onTagContent },
          {
            key: 'add_to_group',
            label: t('pages.devices.bulk_actions.add_to_device_group'),
            Icon: DeviceHub,
            onClick: onAddToGroup,
            adminOnly: !currentNetwork.canManage.value,
          },
          {
            key: 'schedule_show',
            label: t('pages.devices.bulk_actions.schedule_show'),
            Icon: Schedule,
            onClick: onScheduleShow,
          },
          {
            key: 'update_settings',
            label: t('pages.devices.bulk_actions.update_settings'),
            Icon: Settings,
            onClick: onUpdateSettings,
          },
        ],
      },
      // Below: actual device commands, grouped by feature area.
      {
        key: 'content',
        title: t('pages.devices.bulk_actions.groups.content'),
        items: [
          {
            key: 'play_content',
            label: t('pages.devices.bulk_actions.play_content'),
            Icon: CropOriginal,
            onClick: onPlayContent,
          },
          {
            key: 'play_overlay',
            label: t('pages.devices.bulk_actions.play_overlay'),
            Icon: Layers,
            onClick: onPlayOverlay,
          },
          {
            key: 'stop_overlay',
            command: DeviceCommandKind.StopOverlay,
            label: t('common.device_commands.stop_overlay'),
            Icon: LayersClear,
          },
          {
            key: 'tune_channel',
            label: t('pages.devices.bulk_actions.tune_channel'),
            Icon: LiveTv,
            onClick: onTuneChannel,
          },
          {
            key: 'play_view',
            label: t('common.device_commands.trigger_view'),
            Icon: PlayArrow,
            onClick: onTriggerView,
          },
        ],
      },
      {
        key: 'display',
        title: t('pages.devices.bulk_actions.groups.display'),
        items: [
          {
            key: 'set_brightness',
            label: t('pages.devices.bulk_actions.set_brightness'),
            Icon: Brightness6,
            onClick: onBrightness,
          },
          {
            key: 'tv_on',
            ...arbitrary('tv_on'),
            label: t('common.device_commands.tv_on'),
            Icon: Tv,
          },
          {
            key: 'tv_off',
            ...arbitrary('tv_off'),
            label: t('common.device_commands.tv_off'),
            Icon: TvOff,
            confirm: {
              title: t('pages.devices.bulk_actions.confirms.tv_off.title'),
              prompt: t('pages.devices.bulk_actions.confirms.tv_off.prompt'),
            },
          },
        ],
      },
      {
        key: 'audio',
        title: t('pages.devices.bulk_actions.groups.audio'),
        items: [
          {
            key: 'mute',
            ...arbitrary('mute'),
            label: t('common.device_commands.mute'),
            Icon: VolumeOff,
          },
          {
            key: 'unmute',
            ...arbitrary('unmute'),
            label: t('common.device_commands.unmute'),
            Icon: VolumeUp,
          },
          {
            key: 'set_volume',
            label: t('pages.devices.bulk_actions.set_volume'),
            Icon: VolumeUp,
            onClick: onVolume,
          },
        ],
      },
      {
        key: 'input_lock',
        title: t('pages.devices.bulk_actions.groups.input_lock'),
        items: [
          {
            key: 'keys_lock',
            ...arbitrary('keys_lock'),
            label: t('common.device_commands.keys_lock'),
            Icon: KeyboardOff,
          },
          {
            key: 'keys_unlock',
            ...arbitrary('keys_unlock'),
            label: t('common.device_commands.keys_unlock'),
            Icon: Keyboard,
          },
          {
            key: 'remote_lock',
            ...arbitrary('remote_lock'),
            label: t('common.device_commands.remote_lock'),
            Icon: SettingsRemoteOff,
          },
          {
            key: 'remote_unlock',
            ...arbitrary('remote_unlock'),
            label: t('common.device_commands.remote_unlock'),
            Icon: SettingsRemote,
          },
        ],
      },
      {
        key: 'system',
        title: t('pages.devices.bulk_actions.groups.system'),
        items: [
          {
            key: 'purge',
            command: DeviceCommandKind.Purge,
            label: t('common.device_commands.purge'),
            Icon: DeleteSweep,
            destructive: true,
            confirm: {
              title: t('pages.devices.bulk_actions.confirms.purge.title'),
              prompt: t('pages.devices.bulk_actions.confirms.purge.prompt'),
            },
          },
          {
            key: 'reboot',
            command: DeviceCommandKind.Reboot,
            label: t('common.device_commands.reboot'),
            Icon: PowerSettingsNew,
            confirm: {
              title: t('pages.devices.bulk_actions.confirms.reboot.title'),
              prompt: t('pages.devices.bulk_actions.confirms.reboot.prompt'),
            },
          },
          {
            key: 'restart',
            command: DeviceCommandKind.Restart,
            label: t('common.device_commands.restart'),
            Icon: RestartAlt,
            confirm: {
              title: t('pages.devices.bulk_actions.confirms.restart.title'),
              prompt: t('pages.devices.bulk_actions.confirms.restart.prompt'),
            },
          },
          {
            key: 'update_software',
            label: t('pages.devices.bulk_actions.update_software'),
            Icon: OfflineBolt,
            onClick: onUpdateSoftware,
            adminOnly: !currentNetwork.canManage.value,
          },
        ],
      },
      {
        key: 'diagnostics',
        title: t('pages.devices.bulk_actions.groups.diagnostics'),
        items: [
          {
            key: 'analyze_channels',
            command: DeviceCommandKind.AnalyzeChannels,
            label: t('common.device_commands.analyze_channels'),
            Icon: Analytics,
            adminOnly: true,
          },
          {
            key: 'screenshot',
            command: DeviceCommandKind.Screenshot,
            label: t('common.device_commands.screenshot'),
            Icon: PhotoCamera,
          },
          {
            key: 'logs',
            command: DeviceCommandKind.Logs,
            label: t('common.device_commands.logs'),
            Icon: Description,
            adminOnly: true,
          },
        ],
      },
      {
        key: 'advanced',
        title: t('pages.devices.bulk_actions.groups.advanced'),
        adminOnly: true,
        items: [
          {
            key: 'arbitrary',
            label: t('pages.devices.bulk_actions.arbitrary_command'),
            Icon: Send,
            onClick: onSendArbitrary,
          },
          {
            key: 'remote',
            command: DeviceCommandKind.Remote,
            label: t('common.device_commands.remote'),
            Icon: Cast,
          },
        ],
      },
    ],
    [
      currentNetwork.canManage.value,
      onAddToGroup,
      onBrightness,
      onPlayContent,
      onPlayOverlay,
      onScheduleShow,
      onSendArbitrary,
      onTagContent,
      onTriggerView,
      onTuneChannel,
      onUpdateSettings,
      onUpdateSoftware,
      onVolume,
      t,
    ],
  );

  // Apply admin filter + search filter. A group is shown if any of its items survives.
  // Items with neither an onClick handler nor a direct command are hidden — this
  // is how consumer-supplied undefined handlers cause their items to drop out.
  const visibleGroups = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    return groups
      .filter((g) => !g.adminOnly || currentUser.admin)
      .map((g) => ({
        ...g,
        items: g.items
          .filter((i) => {
            if (i.onClick == null && i.command == null) return false;
            if (i.adminOnly && !currentUser.admin) return false;
            if (!trimmed) return true;
            return i.label.toLowerCase().includes(trimmed);
          })
          .map((i) => {
            const reason = disabledItems?.[i.key];
            return reason ? { ...i, disabled: true, disabledReason: reason } : i;
          }),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, query, currentUser.admin, disabledItems]);

  const renderItem = (item: Item): ReactNode => {
    const { Icon } = item;
    const inner = (
      <>
        <ListItemIcon sx={item.destructive ? { color: 'error.main' } : undefined}>
          <Icon fontSize="small" />
        </ListItemIcon>
        <ListItemText primaryTypographyProps={item.destructive ? { color: 'inherit' } : undefined}>
          {item.label}
        </ListItemText>
      </>
    );

    if (item.disabled && item.disabledReason) {
      return (
        <TooltipMenuItem
          key={item.key}
          tooltip={item.disabledReason}
          disabled
          sx={item.destructive ? { color: 'error.main' } : undefined}
        >
          {inner}
        </TooltipMenuItem>
      );
    }

    return (
      <MenuItem
        key={item.key}
        disabled={item.disabled}
        onClick={() => {
          if (item.onClick) {
            item.onClick(selected, onComplete);
            onClose();
            return;
          }
          void runCommand(item);
        }}
        sx={item.destructive ? { color: 'error.main' } : undefined}
      >
        {inner}
      </MenuItem>
    );
  };

  const renderGroup = (group: Group, index: number): ReactNode[] => {
    const nodes: ReactNode[] = [];
    if (index > 0) nodes.push(<Divider key={`${group.key}-divider`} />);
    nodes.push(
      <ListSubheader key={`${group.key}-header`} sx={{ lineHeight: '32px' }}>
        {group.title}
      </ListSubheader>,
    );
    group.items.forEach((item) => nodes.push(renderItem(item)));
    return nodes;
  };

  return (
    <>
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {subject && (
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              flexShrink: 0,
            }}
          >
            <Typography variant="body2" color="text.primary" fontWeight="bold" noWrap>
              {subject}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            px: 1,
            py: 0.75,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder={t('pages.devices.bulk_actions.search_placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />,
            }}
          />
        </Box>
        <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
          {visibleGroups.length === 0 ? (
            <Box sx={{ px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                {t('pages.devices.bulk_actions.no_results')}
              </Typography>
            </Box>
          ) : (
            <MenuList {...focusableDisabledItems} aria-labelledby={labelledBy} id={menuId}>
              {visibleGroups.flatMap(renderGroup)}
            </MenuList>
          )}
        </Box>
      </Popover>
      <ConfirmDialog
        {...confirmProps}
        title={confirmText.title}
        prompt={confirmText.prompt}
        confirm={t('common.actions.continue')}
      />
    </>
  );
};
